import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import InnerLayout from "./InnerLayout";
import { adminStaff } from "../data/mock";
import { undergraduateProgrammes } from "./departments/programmeIndex";
import facultyDirectory from "./departments/UG/facultyData/facultyDirectory";

const tabs = [
  { id: "faculty", label: "Faculty" },
  { id: "admin", label: "Administrative Staff" },
  { id: "non-teaching", label: "Non Teaching Staff" },
];

const People = () => {
  const [params, setParams] = useSearchParams();
  const normalizeTab = (value) => (tabs.some((item) => item.id === value) ? value : "faculty");
  const [tab, setTab] = useState(normalizeTab(params.get("tab")));
  const teachingDepartments = undergraduateProgrammes.filter(
    (programme) => facultyDirectory.some((facultyMember) => facultyMember.deptId === programme.id)
  );
  const nonTeachingDepartments = undergraduateProgrammes.filter(
    (programme) => programme.nonTeachingStaff?.length > 0
  );
  const selectedDept = (() => {
    const value = params.get("dept");
    if (!value) return "all";
    const normalizedValue = value.toLowerCase();
    const matchingDept = teachingDepartments.find(
      (d) => d.id === normalizedValue || d.short.toLowerCase() === normalizedValue
    );
    return matchingDept?.id || "all";
  })();

  useEffect(() => {
    setTab(normalizeTab(params.get("tab")));
  }, [params]);

  const setActive = (id) => {
    setTab(id);
    const p = new URLSearchParams(params);
    p.set("tab", id);
    setParams(p, { replace: true });
  };

  const setDeptFilter = (d) => {
    const p = new URLSearchParams(params);
    if (d === "all") p.delete("dept");
    else p.set("dept", d);
    setParams(p, { replace: true });
  };

  const facultyFiltered = (() => {
    if (selectedDept === "all") return facultyDirectory;
    return facultyDirectory.filter((f) => f.deptId === selectedDept);
  })();

  return (
    <InnerLayout
      title="People @ VDIT"
      image="https://images.pexels.com/photos/8423416/pexels-photo-8423416.jpeg?w=2400"
      breadcrumbs={[{ label: "About", to: "/about" }, { label: "People @ VDIT" }]}
    >
      <div className="flex border-b border-brand/20 mb-8 font-sans-ui overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setActive(t.id)}
            className={`px-5 py-3 text-sm font-semibold tracking-widest whitespace-nowrap transition-colors ${
              tab === t.id
                ? "text-brand border-b-2 border-brand"
                : "text-[#3a3a3a] hover:text-brand"
            }`}
          >
            {t.label.toUpperCase()}
          </button>
        ))}
      </div>

      {tab === "faculty" && (
        <>
          <div className="flex flex-wrap gap-2 mb-6">
            <button
              onClick={() => setDeptFilter("all")}
              className={`px-3 py-1.5 text-xs rounded-full font-sans-ui ${
                selectedDept === "all"
                  ? "bg-brand text-surface"
                  : "bg-white text-brand border border-brand/30"
              }`}
            >
              All
            </button>
            {teachingDepartments.map((d) => (
              <button
                key={d.id}
                onClick={() => setDeptFilter(d.id)}
                className={`px-3 py-1.5 text-xs rounded-full font-sans-ui ${
                  selectedDept === d.id
                    ? "bg-brand text-surface"
                    : "bg-white text-brand border border-brand/30"
                }`}
              >
                {d.short}
              </button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {facultyFiltered.map((f) => {
              const d = undergraduateProgrammes.find((programme) => programme.id === f.deptId);
              return (
                <article
                  key={f.id}
                  className="bg-white border border-brand/15 overflow-hidden card-hover"
                >
                  <Link to={`/faculty/${f.id}`} className="aspect-[3/4] overflow-hidden block bg-white">
                    <img
                      src={f.image}
                      alt={f.name}
                      loading="lazy"
                      className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-700"
                    />
                  </Link>
                  <div className="p-4">
                    <Link to={`/faculty/${f.id}`} className="hover:underline">
                      <h3 className="text-brand font-semibold text-lg leading-snug">
                        {f.name}
                      </h3>
                    </Link>
                    <p className="italic text-sm text-[#3a3a3a]">
                      {f.role}
                      {d && (
                        <>
                          <span className="mx-1">&middot;</span>
                          <span>{d.short}</span>
                        </>
                      )}
                    </p>
                    <Link to={`/faculty/${f.id}`} className="text-xs text-brand underline mt-3 inline-block">
                      View profile &rarr;
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        </>
      )}

      {tab === "admin" && (
        <div className="bg-white border border-brand/15">
          <table className="w-full text-sm">
            <thead className="bg-brand text-surface">
              <tr>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Role</th>
              </tr>
            </thead>
            <tbody>
              {adminStaff.map((a) => (
                <tr key={a.id} className="odd:bg-surface/40">
                  <td className="p-3 border-t border-brand/10">{a.name}</td>
                  <td className="p-3 border-t border-brand/10 italic">{a.role}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "non-teaching" && (
        <div className="space-y-8">
          {nonTeachingDepartments.map((programme) => (
            <section key={programme.id} className="bg-white border border-brand/15">
              <div className="px-5 py-4 border-b border-brand/10">
                <p className="text-xs uppercase tracking-widest text-brand font-sans-ui font-semibold">
                  {programme.short}
                </p>
                <h3
                  className="text-2xl text-brand font-semibold mt-1"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  {programme.name}
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-brand text-surface">
                    <tr>
                      <th className="text-left p-3 w-16">Sl. No.</th>
                      <th className="text-left p-3">Staff Name</th>
                      <th className="text-left p-3">Designation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {programme.nonTeachingStaff.map(([name, designation], index) => (
                      <tr key={`${programme.id}-${name}`} className="odd:bg-surface/40">
                        <td className="p-3 border-t border-brand/10">{index + 1}</td>
                        <td className="p-3 border-t border-brand/10">{name}</td>
                        <td className="p-3 border-t border-brand/10 italic">{designation}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
        </div>
      )}
    </InnerLayout>
  );
};

export default People;
